import {
  addItemToCartMutation,
  deleteCartItemMutation,
} from '@/api/carts/cartMutations';
import { cartQuery } from '@/api/carts/cartQueries';
import { ConfirmDeleteDialog } from '@/components/common/ConfirmDeleteDialog';
import { Pagination } from '@/components/common/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common/Table';
import { cn } from '@/lib/cn';
import { useBreakpoint } from '@/lib/contexts/BreakpointContext';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import { MinusIcon, PlusIcon, XIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

interface CartItemsListProps {
  cartId: number;
}

interface CartQuantityButtonProps {
  Icon: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
}

const CartQuantityButton = ({
  Icon,
  onClick,
  disabled = false,
}: CartQuantityButtonProps) => {
  return (
    <button
      className={cn(
        'text-card bg-primary flex size-6 cursor-pointer items-center justify-center rounded-xs',
        disabled && 'cursor-not-allowed opacity-50',
      )}
      onClick={onClick}
      disabled={disabled}
    >
      <Icon className="w-4" />
    </button>
  );
};

export const CartItemsList = ({ cartId }: CartItemsListProps) => {
  const { isMobile } = useBreakpoint();
  const { data: cart, isLoading } = useQuery(cartQuery(cartId));
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cartItemIdToDelete, setCartItemIdToDelete] = useState<number | null>(
    null,
  );
  const perPage = 6;

  const addItemToCart = useMutation(addItemToCartMutation(cartId));
  const deleteCartItem = useMutation(deleteCartItemMutation(cartId));

  const handleDeleteClick = (cartItemId: number) => {
    setCartItemIdToDelete(cartItemId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (cartItemIdToDelete !== null) {
      deleteCartItem.mutate({ cartItemId: cartItemIdToDelete });
      setDeleteDialogOpen(false);
      setCartItemIdToDelete(null);
    }
  };

  const allItems = cart?.data.items || [];
  const paginatedItems = useMemo(
    () => allItems.slice((currentPage - 1) * perPage, currentPage * perPage),
    [allItems, currentPage],
  );

  useEffect(() => {
    const maxPage = Math.ceil(allItems.length / perPage);
    if (currentPage > maxPage && maxPage > 0) setCurrentPage(1);
  }, [allItems.length, currentPage, perPage]);

  return (
    <div className="flex flex-col gap-4">
      <Table className="[&_td]:whitespace-normal [&_th]:whitespace-normal">
        <TableHeader className="w-full">
          <TableRow className="w-full">
            <TableHead>Supplement</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>30 pills</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className="w-full">
          {paginatedItems.map((cartItem) => (
            <TableRow key={cartItem.id} className="w-full">
              <TableCell>
                {cartItem.item_price.item?.name || 'Supplement'} |{' '}
                {cartItem.item_price.strength}
              </TableCell>
              <TableCell>€{cartItem.item_price.formatted_price}</TableCell>
              <TableCell>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center">
                    <CartQuantityButton
                      Icon={MinusIcon}
                      onClick={() => {
                        if (isMobile && cartItem.quantity === 1) {
                          deleteCartItem.mutate({ cartItemId: cartItem.id });
                        } else {
                          addItemToCart.mutate({
                            price_id: cartItem.price_id,
                            quantity: cartItem.quantity - 1,
                          });
                        }
                      }}
                      disabled={!isMobile && cartItem.quantity === 1}
                    />

                    <p className="size-6 text-center">{cartItem.quantity}</p>

                    <CartQuantityButton
                      Icon={PlusIcon}
                      onClick={() =>
                        addItemToCart.mutate({
                          price_id: cartItem.price_id,
                          quantity: cartItem.quantity + 1,
                        })
                      }
                    />
                  </div>

                  <button
                    onClick={() => handleDeleteClick(cartItem.id)}
                    className="hidden cursor-pointer md:block"
                  >
                    <XIcon className="text-destructive size-5" />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Remove item"
        description="Are you sure you want to remove this item from the cart?"
        onConfirm={handleDeleteConfirm}
        isPending={deleteCartItem.isPending}
      />

      {allItems.length > perPage && (
        <Pagination
          currentPage={currentPage}
          lastPage={Math.ceil(allItems.length / perPage)}
          total={allItems.length}
          perPage={perPage}
          onPageChange={setCurrentPage}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};
